import { useMutation, useQuery, useQueryClient } from "react-query";

import { ProjectStatus } from "../../../types";
import FetchClients, { ClientOutput, ClientPaths } from "../client";
import { getProjectsInProgressQueryKey } from "./inProgress";
import { useInvalidateEquipmentQueries } from "../equipment/useInvalidateEquipmentQueries";
import { useCurrentAccount } from "@src/backpack-console/CurrentAccountManager";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";
import { useCallback } from "react";
import request from "@src/global_functions/request";

export type BackpackProject = ClientOutput["projects"]["Project"];
export type BackpackProjectRequest = ClientOutput["projects"]["ProjectRequest"];
export type ProjectNote = ClientOutput["projects"]["Note"];
export type PortfolioProjectsSummaryFiltersResponse =
  ClientOutput["projects"]["PortfolioProjectsSummaryFilters"];
export type PortfolioProjectsSummary =
  ClientOutput["projects"]["PortfolioProjectsSummary"];
export type SiteProjectsSummary =
  ClientOutput["projects"]["SiteProjectsSummary"];

export type PortfolioProjectsQueryParamFilters = Omit<
  NonNullable<
    ClientPaths["projects"]["/portfolio/summary"]["get"]["parameters"]["query"]
  >,
  "account_id" | "status" | "sites"
>;

const project = {
  useQueryAllForSite,
  useQueryPortfolioSummaryFilters,
  useQueryAccountSummaryRollup,
  useQueryAccountSitesSummaryRollup,
  useQueryProjectTags,
  mutations: {
    usePost,
    usePatch,
    useDelete,
    useBulkDelete,
    usePostBulkUpdateStatus,
  },
} as const;

export default project;

export const PROJECT_KEY = "projects/project";

const getBackpackProjects = async (
  siteId: number | null,
  status?: ProjectStatus
) => {
  // query is not enabled if siteId is not a number, so error should never throw here
  if (typeof siteId !== "number") {
    throw new Error("siteId is required");
  }
  const { data, error } = await FetchClients.projects.GET("/", {
    params: {
      query: {
        site_id: siteId,
        project_status: status,
      },
    },
  });
  if (data) return data;
  throw new Error(error);
};

function useQueryAllForSite(siteId: number | null, status?: ProjectStatus) {
  return useQuery(
    [PROJECT_KEY, siteId, status],
    () => getBackpackProjects(siteId, status),
    { enabled: typeof siteId === "number" }
  );
}

function usePost(siteId: number) {
  const queryClient = useQueryClient();
  const { accountId } = useCurrentAccount();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();
  return useMutation({
    mutationFn: async (body: BackpackProjectRequest) => {
      const { data, error } = await FetchClients.projects.POST("/", {
        body,
      });
      if (data) return data;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw new Error(error);
    },
    onSuccess: async (_, req) => {
      await queryClient.invalidateQueries({
        queryKey: [PROJECT_KEY, siteId],
      });
      await invalidatePortfolioSummary();
      await invalidateEquipmentQueries({
        accountId,
        siteId,
        equipmentIds: req.linked_equipment,
      });
    },
    onError: (e) => {
      throw new Error(
        `Unable to create new project for site: ${siteId}
        ${(e as Error)?.message || "Unknown Error"}`
      );
    },
  });
}

export type PutProjectRequest = Partial<BackpackProjectRequest> & {
  id: number; // Project ID
};

function usePatch(accountId?: number) {
  const queryClient = useQueryClient();
  const invalidateEquipmentQueries = useInvalidateEquipmentQueries();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();

  return useMutation({
    mutationFn: async ({ id, ...body }: PutProjectRequest) => {
      const { response, error } = await FetchClients.projects.PATCH("/", {
        params: {
          query: {
            id,
          },
        },
        // @ts-expect-error
        body,
      });
      if (response.ok) return;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw new Error(error);
    },
    onSuccess: async (_, req) => {
      await queryClient.invalidateQueries({
        queryKey: [PROJECT_KEY, req.site_id],
      });
      await invalidateEquipmentQueries({
        accountId,
        siteId: req.site_id,
        equipmentIds: req.linked_equipment,
      });
      await invalidatePortfolioSummary();
    },
  });
}

function useDelete(siteId: number) {
  const queryClient = useQueryClient();
  const queryKey = [PROJECT_KEY, siteId];
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutation({
    mutationFn: async (id: number) => {
      const { error, response } = await FetchClients.projects.DELETE("/", {
        params: {
          query: {
            id,
          },
        },
      });
      if (response.ok) return;
      throw new Error(error);
    },
    onMutate: async () => {
      // cancel queries
      await queryClient.cancelQueries({
        queryKey,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey,
      });
      await invalidatePortfolioSummary();
    },
  });
}

function useBulkDelete(siteId: number) {
  const queryClient = useQueryClient();
  const queryKey = [PROJECT_KEY, siteId];
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const { error, response } = await FetchClients.projects.DELETE(
        "/bulk_delete",
        {
          params: {
            query: {
              ids,
            },
          },
        }
      );
      if (response.ok) return;
      throw new Error(error);
    },
    onMutate: async () => {
      // cancel queries
      await queryClient.cancelQueries({
        queryKey,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey,
      });
      await invalidatePortfolioSummary();
    },
  });
}

type ProjectStatusRequest = ClientOutput["projects"]["ProjectStatusRequest"];
function usePostBulkUpdateStatus(accountId?: number) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSummary = useInvalidateProjectsPortfolioSummary();

  return useMutation({
    mutationFn: async (statuses: ProjectStatusRequest[]) => {
      const { error, response } = await FetchClients.projects.POST(
        "/bulk-update-status",
        {
          body: statuses,
        }
      );
      if (response.ok) return;
      throw new Error(error);
    },
    onMutate: async () => {
      // cancel queries
      await queryClient.cancelQueries({
        queryKey: [PROJECT_KEY],
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [PROJECT_KEY],
      });
      await invalidatePortfolioSummary();
      if (accountId) {
        await queryClient.invalidateQueries({
          queryKey: getProjectsInProgressQueryKey(accountId),
        });
      }
    },
  });
}

const PROJECTS_PORTFOLIO_SUMMARY_KEY = "projects/portfolio-summary";

function useQueryPortfolioSummaryFilters(accountId: number) {
  return useQuery({
    queryKey: [PROJECTS_PORTFOLIO_SUMMARY_KEY, accountId, "get-filters"],
    queryFn: async () => {
      const { data, error } = await FetchClients.projects.GET(
        "/portfolio/summary/filters",
        {
          params: {
            query: {
              account_id: accountId,
            },
          },
        }
      );

      if (data) return data;
      throw new Error(error);
    },
  });
}

function useQueryAccountSummaryRollup(
  accountId: number,
  filters?: PortfolioProjectsQueryParamFilters
) {
  return useQuery({
    queryKey: [
      PROJECTS_PORTFOLIO_SUMMARY_KEY,
      accountId,
      "portfolio-rollup",
      filters,
    ],
    queryFn: async () => {
      const { data, error } = await FetchClients.projects.GET(
        "/portfolio/summary",
        {
          params: {
            query: {
              account_id: accountId,
              ...filters,
            },
          },
        }
      );

      if (data) return data;
      throw new Error(error);
    },
  });
}

function useQueryAccountSitesSummaryRollup(
  accountId: number,
  filters?: PortfolioProjectsQueryParamFilters
) {
  return useQuery({
    queryKey: [
      PROJECTS_PORTFOLIO_SUMMARY_KEY,
      accountId,
      "sites-rollup",
      filters,
    ],
    queryFn: async () => {
      const { data, error } = await FetchClients.projects.GET(
        "/portfolio/summary/sites",
        {
          params: {
            query: {
              account_id: accountId,
              ...filters,
            },
          },
        }
      );

      if (data) return data;
      throw new Error(error);
    },
  });
}

export type ProjectTag = {
  id: number;
  name: string;
  label: string;
};

function useQueryProjectTags() {
  return useQuery({
    queryKey: ["project-tags"],
    queryFn: async () => {
      const res = await request.get("/rest/tags");
      if (res.ok) return res.body as ProjectTag[];
      throw res.error;
    },
  });
}

export function useInvalidateProjectsPortfolioSummary() {
  const queryClient = useQueryClient();
  const { accountId } = useCurrentAccount();

  return useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: [PROJECTS_PORTFOLIO_SUMMARY_KEY, accountId],
    });
  }, [queryClient, accountId]);
}
